import React, { useEffect } from "react";
import { Select } from "@outpave/ds";
import { titleCase } from "title-case";
import { useTranslation } from "react-i18next";
import useGetBankAccounts, {
  BankAccount,
  UseGetBankAccountsResult,
} from "hooks/useGetBankAccounts";
import AccountType from "enums/AccountType";

interface FinancialAccountsDropdownProps {
  value: string;
  onChange: (account: BankAccount | null) => void;
  /** Bump to refetch the account list after something upstream changes it. */
  refetchTrigger?: unknown;
  accountTypes?: AccountType[];
  includeAllOption?: boolean;
  width?: number | string;
  showTitle?: boolean;
  /**
   * A result the parent already fetched. Without it this self-fetches, which
   * is a second account request whose options can drift from the parent's.
   */
  accounts?: UseGetBankAccountsResult;
}

export const ALL_ACCOUNTS_OPTION = {
  id: "allAccounts",
  nickname: "All Accounts",
};

const FinancialAccountsDropdown: React.FC<FinancialAccountsDropdownProps> = ({
  value,
  onChange,
  refetchTrigger,
  accountTypes = [
    AccountType.FINANCIAL_ACCOUNT,
    AccountType.PLAID_EXTERNAL_CARD_ACCOUNT,
  ],
  includeAllOption = true,
  width,
  showTitle = true,
  accounts,
}) => {
  const { t } = useTranslation();
  const own = useGetBankAccounts({
    accountTypes,
    withBalances: false,
    skip: Boolean(accounts),
  });
  const { allAccounts, refetchAllAccounts } = accounts ?? own;

  // The sentinel row is a UI literal and translates; an account's nickname is
  // runtime data and must not, because i18next reads ":" as a namespace
  // separator and would render "Ops: West" as " West" (client/AGENTS.md).
  const options = [
    ...(includeAllOption
      ? [{ id: ALL_ACCOUNTS_OPTION.id, label: t(ALL_ACCOUNTS_OPTION.nickname) }]
      : []),
    ...allAccounts.map((account) => ({
      id: account.id,
      label: titleCase(
        (account.nickname || account.bankName || t("Unknown")).toLowerCase(),
      ),
    })),
  ];

  // The DS clears to null, and "no account" is a real selection here rather
  // than an empty one, so a clear falls back to the All Accounts row.
  const handleChange = (selectedId: string | null) => {
    if (!selectedId || selectedId === ALL_ACCOUNTS_OPTION.id) {
      onChange(null);
      return;
    }
    onChange(allAccounts.find((acc) => acc.id === selectedId) ?? null);
  };

  useEffect(() => {
    // Only for the list this component fetched: refetching a parent's result
    // from here would fight whoever owns it.
    if (!accounts) refetchAllAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchTrigger, accounts]);

  return (
    <Select
      size="md"
      options={options}
      // Falling back to the sentinel when it was not offered would leave the
      // Select controlled by a value that is not among its options, and would
      // show an all-accounts state a caller explicitly disabled.
      value={value || (includeAllOption ? ALL_ACCOUNTS_OPTION.id : "")}
      onChange={handleChange}
      clearable={false}
      label={showTitle ? t("Select Account") : undefined}
      // A label-less combobox has no accessible name otherwise, and the name
      // has to sit on the input rather than a wrapper.
      aria-label={showTitle ? undefined : t("Select Account")}
      width={width}
    />
  );
};

export default FinancialAccountsDropdown;
